import { useState } from "react";
import { safeFormat } from '@/lib/safeDate';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, Clock, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

interface RescheduleCallModalProps {
  open: boolean;
  onClose: () => void;
  call: {
    id: string;
    title?: string;
    scheduled_for?: string;
    channel?: string;
    rescheduled_count?: number;
  };
  onReschedule: (callId: string, newScheduledFor: string, reason?: string) => Promise<void>;
}

export function RescheduleCallModal({
  open,
  onClose,
  call,
  onReschedule,
}: RescheduleCallModalProps) {
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newDate) {
      toast.error("Por favor, selecione a nova data");
      return;
    }

    // Se sem horário definido, usar T12:00:00Z (padrão "sem horário" do sistema)
    const newScheduledFor = newTime
      ? `${newDate}T${newTime}:00`
      : `${newDate}T12:00:00Z`;

    // Validação: se houver horário, deve ser no futuro.
    // Sem horário: aceitar qualquer data >= hoje.
    if (newTime) {
      if (new Date(newScheduledFor) <= new Date()) {
        toast.error("A nova data/hora deve ser no futuro");
        return;
      }
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const picked = new Date(`${newDate}T00:00:00`);
      if (picked < today) {
        toast.error("A nova data deve ser hoje ou no futuro");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onReschedule(call.id, newScheduledFor, reason || undefined);
      toast.success("Chamada remarcada com sucesso!");
      onClose();
      // Reset form
      setNewDate("");
      setNewTime("");
      setReason("");
    } catch (error) {
      console.error("Error rescheduling call:", error);
      toast.error("Erro ao remarcar chamada");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentScheduledFor = call.scheduled_for 
    ? safeFormat(new Date(call.scheduled_for), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : "Não agendada";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-amber-600" />
            Remarcar Chamada
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Schedule Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Clock className="w-4 h-4" />
              Horário Atual
            </div>
            <p className="text-sm text-slate-600 font-medium">{currentScheduledFor}</p>
            {call.title && (
              <p className="text-xs text-slate-500">{call.title}</p>
            )}
            {(call.rescheduled_count ?? 0) > 0 && (
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <AlertCircle className="w-3 h-3" />
                Já remarcada {call.rescheduled_count}x
              </div>
            )}
          </div>

          {/* New Schedule */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Novo Horário</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="new-date" className="text-xs text-slate-500">Data</Label>
                <Input
                  id="new-date"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={safeFormat(new Date(), "yyyy-MM-dd")}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-time" className="text-xs text-slate-500">Horário (opcional)</Label>
                <Input
                  id="new-time"
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Reason (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Motivo da Remarcação <span className="text-slate-400">(opcional)</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Cliente pediu para remarcar, conflito de agenda..."
              className="min-h-[80px] resize-none"
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !newDate}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {isSubmitting ? "Remarcando..." : "Remarcar Chamada"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
